import { HttpRequest } from '@angular/common/http';
import { makeStateKey } from '@angular/core';
import { Category } from '../models/category.model';
import { flattenCategories } from './shop-by-category.util';

/** Marks that prerender transferred an anonymous Cms/Home payload. */
export const HOME_BUNDLE_TRANSFERRED = makeStateKey<boolean>('zt-cms-home');

/** Above-the-fold homepage snapshot — never the full product rails. */
export const HOME_BUNDLE_SSR = makeStateKey<SlimHomeBundle>('zt-cms-home-ssr');

export interface SlimHomeBundle {
  sections: Array<{ products: []; productIds: [] }>;
  advertisements: unknown[];
  categories: Category[];
  homepageSettings: { shopByCategoryIds: number[] };
  featuredBlockOrder: number;
  featuredProducts: [];
}

/**
 * Full Cms/Home JSON is ~250KB and must not be serialized into index.html.
 * Keep hero ads + shop-by category cards; refill rails from the network after hydration.
 *
 * Returns null when the bundle is empty (e.g. prerender hit a 503) so we never bake a
 * poisoned TransferState that flashes the wrong hero and blocks CMS reveals.
 */
export function slimHomeBundleForTransfer(bundle: {
  sections?: readonly unknown[];
  advertisements?: unknown[];
  categories?: Category[] | null;
  homepageSettings?: { shopByCategoryIds?: number[] } | null;
  featuredBlockOrder?: number;
  featuredProducts?: unknown[];
}): SlimHomeBundle | null {
  const shopByCategoryIds = (bundle.homepageSettings?.shopByCategoryIds ?? []).filter(
    (id) => Number.isFinite(id) && id > 0
  );
  const wanted = new Set(shopByCategoryIds);
  const categories = flattenCategories(bundle.categories)
    .filter((category) => wanted.has(category.id))
    .map((category) => ({
      id: category.id,
      name: category.name,
      nameEn: category.nameEn,
      nameAr: category.nameAr,
      imageUrl: category.imageUrl,
      categoryGroupId: category.categoryGroupId,
      parentCategoryId: category.parentCategoryId,
      sortOrder: category.sortOrder,
      showOnNavigation: category.showOnNavigation,
    }));

  const advertisements = Array.isArray(bundle.advertisements) ? bundle.advertisements : [];
  const sections = (bundle.sections ?? []) as Array<{ products?: unknown[]; productIds?: unknown[] }>;

  if (!advertisements.length && !categories.length && !sections.length) {
    return null;
  }

  return {
    advertisements,
    categories,
    homepageSettings: { shopByCategoryIds },
    featuredBlockOrder: Number.isFinite(Number(bundle.featuredBlockOrder))
      ? Number(bundle.featuredBlockOrder)
      : 999,
    featuredProducts: [],
    sections: sections.map((section) => ({
      ...section,
      products: [] as [],
      productIds: [] as [],
    })),
  } as SlimHomeBundle;
}

/** True when a transferred slim snapshot can drive first paint (hero and/or shop-by). */
export function isUsefulHomeSlim(slim: SlimHomeBundle | null | undefined): boolean {
  if (!slim) {
    return false;
  }
  return (slim.advertisements?.length ?? 0) > 0 || (slim.categories?.length ?? 0) > 0;
}

/** Cache only guest homepage JSON — kept for tests / callers; HTTP transfer itself is disabled. */
export function isAnonymousCmsHomeRequest(req: HttpRequest<unknown>): boolean {
  if (req.method !== 'GET' && req.method !== 'HEAD') {
    return false;
  }
  const url = req.url || '';
  if (!/Cms\/Home(?:[/?]|$)/i.test(url)) {
    return false;
  }
  if (req.params?.has('userId')) {
    return false;
  }
  return !/[?&]userId=/.test(req.urlWithParams || url);
}

/** Transfer PDP/PLP GET bodies so SSR HTML hydrates without a second catalog fetch. Homepage stays slim. */
export function shouldHttpTransferStorefrontRequest(req: HttpRequest<unknown>): boolean {
  if (req.method !== 'GET' && req.method !== 'HEAD') {
    return false;
  }
  const url = req.url || '';
  return (
    /Products\/GetProductById(?:[/?]|$)/i.test(url) ||
    /Products\/GetProductsByCategory(?:[/?]|$)/i.test(url) ||
    /Categories\/GetCategoryTree(?:[/?]|$)/i.test(url)
  );
}
